import { Modal, ModalProps } from "antd"
import { useTranslation } from "react-i18next"
import { object, string } from "yup"
import { type Resolver, Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { FormControl } from "@atoms"
import RatingEmojis from "components/rating-emojis"
import TextArea from "antd/es/input/TextArea"
import { useTerminateEvent } from "pages/events/queries"
import { useNavigate } from "react-router-dom"

interface Props extends ModalProps {
  data?: string
  onClose: () => void
}

const TerminateEventModal: React.FC<Props> = ({ data, ...props }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const form = useTerminateEventForm()
  const {
    reset,
    control,
    formState: { errors },
    handleSubmit,
  } = form

  const onClose = () => {
    props.onClose()
    reset()
  }

  const terminateEvent = useTerminateEvent(data!, {
    onSuccess() {
      onClose()
      navigate("/")
    },
    meta: {
      entity: t("events.event"),
      type: "terminate",
      context: "male",
      count: 1,
    },
  })

  const onSubmit = (data: any) => {
    terminateEvent.mutate(data)
  }

  return (
    <Modal
      title={t("events.terminate_event")}
      onCancel={onClose}
      okText={t("global.save")}
      cancelText={t("global.cancel")}
      onOk={handleSubmit(onSubmit)}
      confirmLoading={terminateEvent.isLoading}
      {...props}
    >
      <form>
        <FormControl>
          <Controller
            name="emoji"
            control={control}
            render={({ field }) => {
              return (
                <RatingEmojis
                  value={field.value}
                  onClick={(rate) => field.onChange(rate)}
                />
              )
            }}
          />
          {errors.emoji && (
            <FormControl.HelperText>
              {errors.emoji.message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <FormControl>
          <Controller
            name="feedback"
            control={control}
            render={({ field }) => (
              <TextArea
                placeholder={t("events.comment_for_parent")}
                rows={4}
                {...field}
              />
            )}
          />
        </FormControl>
        <FormControl>
          <Controller
            name="admin_feedback"
            control={control}
            render={({ field }) => (
              <TextArea
                placeholder={t("events.comment_for_admin")}
                rows={4}
                {...field}
              />
            )}
          />
        </FormControl>
      </form>
    </Modal>
  )
}

export default TerminateEventModal

interface ITerminateEventForm {
  emoji: "very bad" | "bad" | "neutral" | "good" | "very good" | null
  feedback: string
  admin_feedback: string
}

export function useTerminateEventForm(
  defaultValues?: Partial<ITerminateEventForm>
) {
  const schema = object({
    emoji: string().nullable(),
    feedback: string().nullable(),
    admin_feedback: string().nullable(),
  }).required()

  return useForm<ITerminateEventForm>({
    resolver: yupResolver(schema) as unknown as Resolver<ITerminateEventForm>,
    defaultValues,
  })
}
